import { FormBuilderType, FormBuilderState } from "../FormBuilder.types";

/**
 * Validates the form state against the provided form configuration.
 *
 * @param F The form configuration object.
 * @param state The current state of the form.
 * @param submitted Flag indicating if the form is being submitted.
 * @returns Object containing the validation state and a flag if there are errors.
 */
export function validateForm<FType>(
  F: FormBuilderType<FType>,
  state: FormBuilderState<FType>,
  submitted = false
): {
  hasErrors: boolean;
  validatedState: FormBuilderState<FType>;
} {
  let hasErrors = false;
  const validatedState: FormBuilderState<FType> = {} as FormBuilderState<FType>;

  Object.keys(F).forEach((key) => {
    const k = key as keyof FType;
    const control = F[k];
    let error: string | null = null;

    if (control.validators) {
      for (const validator of control.validators) {
        const validationResult = validator(state[k].value as string, control.label || key, state);
        if (!validationResult.valid) {
          error = validationResult.error || null;
          hasErrors = true;
          break;
        }
      }
    }

    validatedState[k] = {
      ...state[k],
      error,
      isTouched: submitted || state[k].isTouched,
      isSubmitted: !!submitted,
    };
  });

  return { hasErrors, validatedState };
}

/**
 * Constructs the payload for form submission, applying any necessary transformations.
 *
 * @param F The form configuration object.
 * @param FS The current state of the form.
 * @returns The payload object to be submitted.
 */
export function populatePayload<FType>(F: FormBuilderType<FType>, FS: FormBuilderState<FType>) {
  const fPayload: FType = {} as FType;

  Object.keys(FS).forEach((key) => {
    const k = key as keyof FType;
    const controlFormState = FS[k];
    let v = controlFormState.value;
    if (typeof v === "string") {
      v = v.trim() as FType[keyof FType];
    }
    const parse = F[k].parseOnSubmit;
    fPayload[k] = parse ? parse(v) : v;
  });
  return fPayload;
}

/**
 * Resets the form state to its initial state based on the form configuration.
 *
 * @param F The form configuration object.
 * @returns The reset state of the form.
 */
export function cleanState<FType>(F: FormBuilderType<FType>): FormBuilderState<FType> {
  const state: FormBuilderState<FType> = {} as FormBuilderState<FType>;

  Object.keys(F).forEach((key) => {
    const k = key as keyof FType;
    state[k] = {
      value: F[k].defaultValue,
      error: null,
      isTouched: false,
      isSubmitted: false,
      required: F[k].validators && F[k].validators?.some((v) => v.toString().includes("${field} is required.")),
    };
  });
  return state;
}

/**
 * Builds the initial state for the form based on the form configuration and provided entity.
 *
 * @param F The form configuration object.
 * @param entity The initial values for the form fields.
 * @returns The initial state of the form.
 */
export function buildState<FType>(F: FormBuilderType<FType>, entity: FType): FormBuilderState<FType> {
  const state: FormBuilderState<FType> = {} as FormBuilderState<FType>;

  Object.keys(F).forEach((key) => {
    const k = key as keyof FType;
    state[k] = {
      value:
        typeof entity[k] !== "undefined"
          ? typeof entity[k] === "string"
            ? ((entity[k] as string).trim() as FType[keyof FType])
            : entity[k]
          : F[k].defaultValue,
      error: null,
      isTouched: false,
      isSubmitted: false,
      required: F[k].validators && F[k].validators?.some((v) => v.toString().includes("${field} is required.")),
    };
  });
  return state;
}
